import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Select,
  MenuItem,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import PublishIcon from "@mui/icons-material/Publish";
import { getCheckInOutData, getBallkids, getActiveGroupSlug } from "../firebaseService";
import { doc, setDoc, getDoc, deleteDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { useTournamentConfig } from "../context/TournamentConfigContext";
import { useGroup } from "../context/GroupContext";

const getBillingSettingsKey = (slug) => `billing_settings_${slug || "default"}`;

// Einstellungen aus localStorage laden (schneller Cache)
const loadSettingsFromStorage = (slug) => {
  try {
    const saved = localStorage.getItem(getBillingSettingsKey(slug));
    if (saved) return JSON.parse(saved);
  } catch (e) {}
  return null;
};

const saveSettingsToStorage = (settings, slug) => {
  try {
    localStorage.setItem(getBillingSettingsKey(slug), JSON.stringify(settings));
  } catch (e) {}
};

// Firebase: Einstellungen persistent speichern
const loadSettingsFromFirebase = async (slug) => {
  try {
    const snap = await getDoc(doc(db, `${slug}__billing_settings`, "settings"));
    return snap.exists() ? snap.data() : null;
  } catch (e) {
    return null;
  }
};

const saveSettingsToFirebase = async (settings, slug) => {
  try {
    await setDoc(doc(db, `${slug}__billing_settings`, "settings"), {
      ...settings,
      updatedAt: new Date().toISOString(),
    });
  } catch (e) {
    console.error("Fehler beim Speichern der Billing-Settings in Firebase:", e);
  }
};

// Normalize name for matching: lowercase, collapse whitespace, trim
const normalizeName = (name) =>
  (name || "").toLowerCase().replace(/\s+/g, " ").trim();

const AttendanceBilling = () => {
  const { daysOfWeek, stundenlohn = 13.90, rateOptions = [6.95, 13.90], bonusOptions = [0, 6.95, 13.90, 20.85] } = useTournamentConfig();
  const { activeGroup } = useGroup();
  const groupSlug = activeGroup?.slug || "default";
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const HOURLY_RATE_OPTIONS = [13.90, 15.00, 17.00];
  const localSettings = loadSettingsFromStorage(groupSlug);
  const [rates, setRates] = useState(localSettings?.rates || {});
  const [threshold, setThreshold] = useState(localSettings?.threshold || 0);
  const [bonuses, setBonuses] = useState(localSettings?.bonuses || {});
  const [hourlyRate, setHourlyRate] = useState(localSettings?.hourlyRate ?? stundenlohn);
  const [payments, setPayments] = useState([]);
  const [emailMapping, setEmailMapping] = useState({});
  const [excelFile, setExcelFile] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [unmatchedNames, setUnmatchedNames] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [ballkids, checkInOutData, fbSettings] = await Promise.all([
        getBallkids(),
        getCheckInOutData(),
        loadSettingsFromFirebase(groupSlug),
      ]);

      // Firebase-Settings haben Prioritaet ueber localStorage
      if (fbSettings) {
        if (fbSettings.rates) setRates(fbSettings.rates);
        if (fbSettings.threshold != null) setThreshold(fbSettings.threshold);
        if (fbSettings.bonuses) setBonuses(fbSettings.bonuses);
        if (fbSettings.hourlyRate != null) setHourlyRate(fbSettings.hourlyRate);
        saveSettingsToStorage(fbSettings, groupSlug);
      }
      setSettingsLoaded(true);

      // Schnellere Lookup-Map für Check-in-Daten
      const checkInMap = {};
      checkInOutData.forEach((entry) => {
        checkInMap[`${entry.ballkidId}_${entry.day}`] = entry;
      });

      const processedData = ballkids.map((bk) => {
        const attendanceByDay = daysOfWeek.map((day) => {
          const record = checkInMap[`${bk.id}_${day}`];
          if (record?.checkIn && record?.checkOut) {
            const checkInTime = new Date(record.checkIn);
            const checkOutTime = new Date(record.checkOut);
            return ((checkOutTime - checkInTime) / (1000 * 60 * 60)).toFixed(2);
          }
          return "0";
        });
        return { id: bk.id, name: bk.name, attendanceByDay };
      });

      const sortedData = processedData.sort((a, b) => a.name.localeCompare(b.name));
      setAttendanceData(sortedData);
      setLoading(false);
    };
    fetchData();
  }, [daysOfWeek]);

  // Auto-Berechnung bei Daten- oder Einstellungsänderung
  const calculatePayments = useCallback(() => {
    if (attendanceData.length === 0) return;

    const paymentResults = attendanceData.map((row) => {
      const bonus = parseFloat(bonuses[row.name] || 0);
      let total = bonus;

      const dailyPayments = row.attendanceByDay.map((hours, index) => {
        const day = daysOfWeek[index];
        const hoursWorked = parseFloat(hours);
        const rate = rates[day] || 0;
        let payment = 0;

        if (hoursWorked >= 1) {
          payment = hoursWorked >= threshold ? rate : rate / 2;
        }

        total += payment;
        return payment.toFixed(2);
      });

      return { id: row.id, name: row.name, dailyPayments, bonus, total: total.toFixed(2) };
    });

    setPayments(paymentResults);
  }, [attendanceData, rates, threshold, bonuses, daysOfWeek]);

  useEffect(() => {
    calculatePayments();
  }, [calculatePayments]);

  // Einstellungen bei Änderung speichern (localStorage + Firebase)
  useEffect(() => {
    if (!settingsLoaded) return;
    const settings = { rates, threshold, bonuses, hourlyRate };
    saveSettingsToStorage(settings, groupSlug);
    saveSettingsToFirebase(settings, groupSlug);
  }, [rates, threshold, bonuses, hourlyRate, groupSlug, settingsLoaded]);

  const handleRateChange = (day, value) => {
    setRates((prev) => ({ ...prev, [day]: parseFloat(value) }));
  };

  const handleBonusChange = (name, value) => {
    setBonuses((prev) => ({ ...prev, [name]: value }));
  };

  // Alle Tage auf gleichen Betrag setzen
  const setAllRates = (value) => {
    const newRates = {};
    daysOfWeek.forEach((day) => { newRates[day] = parseFloat(value); });
    setRates(newRates);
  };

  // Abrechnung an Ballkinder veröffentlichen
  const publishBilling = async () => {
    try {
      await Promise.all(
        payments.map((p) =>
          setDoc(doc(db, `${getActiveGroupSlug()}__billing_public`, p.id), {
            name: p.name,
            totalDays: p.dailyPayments.filter((d) => parseFloat(d) > 0).length,
            totalAmount: parseFloat(p.total),
            bonus: p.bonus,
            publishedAt: new Date().toISOString(),
          })
        )
      );

      // showBilling in page config aktivieren
      const configRef = doc(db, `${getActiveGroupSlug()}__page_config`, "published");
      const configSnap = await getDoc(configRef);
      if (configSnap.exists()) {
        await setDoc(configRef, { ...configSnap.data(), showBilling: true }, { merge: true });
      }
      const currentRef = doc(db, `${getActiveGroupSlug()}__page_config`, "current");
      const currentSnap = await getDoc(currentRef);
      if (currentSnap.exists()) {
        await setDoc(currentRef, { ...currentSnap.data(), showBilling: true }, { merge: true });
      }

      setSnackbar({ open: true, message: `Abrechnung für ${payments.length} Mitglieder veröffentlicht!`, severity: "success" });
    } catch (error) {
      console.error("Fehler beim Veröffentlichen:", error);
      setSnackbar({ open: true, message: "Fehler beim Veröffentlichen", severity: "error" });
    }
  };

  // Abrechnung zuruecknehmen
  const unpublishBilling = async () => {
    try {
      const billingCol = `${getActiveGroupSlug()}__billing_public`;
      const snapshot = await getDocs(collection(db, billingCol));
      await Promise.all(snapshot.docs.map((d) => deleteDoc(d.ref)));

      // showBilling in page config deaktivieren
      for (const docId of ["published", "current"]) {
        const ref = doc(db, `${getActiveGroupSlug()}__page_config`, docId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          await setDoc(ref, { ...snap.data(), showBilling: false }, { merge: true });
        }
      }

      setSnackbar({ open: true, message: "Abrechnung zurueckgenommen - nicht mehr sichtbar fuer Ballkinder.", severity: "info" });
    } catch (error) {
      console.error("Fehler beim Zuruecknehmen:", error);
      setSnackbar({ open: true, message: "Fehler beim Zuruecknehmen", severity: "error" });
    }
  };

  // E-Mails aus Firebase laden
  useEffect(() => {
    const loadEmails = async () => {
      try {
        const snap = await getDoc(doc(db, `${groupSlug}__email_mapping`, "mapping"));
        if (snap.exists() && snap.data().emails) {
          setEmailMapping(snap.data().emails);
        }
      } catch (e) {
        console.error("Fehler beim Laden der E-Mail-Zuordnung:", e);
      }
    };
    loadEmails();
  }, [groupSlug]);

  // Lookup email by name (normalized matching + reversed name fallback)
  const lookupEmail = useCallback((name) => {
    if (!name) return null;
    const normalized = normalizeName(name);

    // Direct normalized match
    for (const [key, email] of Object.entries(emailMapping)) {
      if (normalizeName(key) === normalized) return email;
    }

    // Try reversed name (e.g. "Mustermann Max" vs "Max Mustermann")
    const parts = normalized.split(" ");
    if (parts.length === 2) {
      const reversed = `${parts[1]} ${parts[0]}`;
      for (const [key, email] of Object.entries(emailMapping)) {
        if (normalizeName(key) === reversed) return email;
      }
    }

    return null;
  }, [emailMapping]);

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    setExcelFile(file);
    const buffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    const sheet = workbook.worksheets[0];

    // Header lesen um Spalten zu erkennen
    const headers = [];
    sheet.getRow(1).eachCell((cell, colNumber) => {
      headers[colNumber] = String(cell.value || "").trim().toLowerCase();
    });

    // Spalten-Indizes finden
    const vornameIdx = headers.findIndex((h) =>
      h === "vorname" || h === "first name" || h === "firstname"
    );
    const nachnameIdx = headers.findIndex((h) =>
      h === "nachname" || h === "last name" || h === "lastname" || h === "familienname"
    );
    const nameIdx = headers.findIndex((h) =>
      h === "name" || h === "vollname" || h === "fullname"
    );
    const emailIdx = headers.findIndex((h) =>
      h === "email" || h === "e-mail" || h === "mail"
    );

    const mapping = {};
    sheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;

      let name;
      if (vornameIdx > 0 && nachnameIdx > 0) {
        const vorname = String(row.getCell(vornameIdx).value || "").trim();
        const nachname = String(row.getCell(nachnameIdx).value || "").trim();
        name = `${vorname} ${nachname}`;
      } else if (nameIdx > 0) {
        name = String(row.getCell(nameIdx).value || "").trim();
      } else {
        // Fallback: Spalte 1 = Name, Spalte 2 = Email
        name = String(row.getCell(1).value || "").trim();
      }

      const email = emailIdx > 0
        ? String(row.getCell(emailIdx).value || "").trim()
        : String(row.getCell(2).value || "").trim();

      if (name && email && email.includes("@")) mapping[name] = email;
    });

    // Merge with existing mapping (new entries overwrite)
    const merged = { ...emailMapping, ...mapping };
    setEmailMapping(merged);

    // Persist to Firebase
    try {
      await setDoc(doc(db, `${groupSlug}__email_mapping`, "mapping"), {
        emails: merged,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error("Fehler beim Speichern der E-Mail-Zuordnung:", err);
    }

    // Check which ballkid names have no match
    const unmatched = attendanceData
      .filter((bk) => {
        const n = normalizeName(bk.name);
        return !Object.keys(merged).some((key) => {
          const nk = normalizeName(key);
          if (nk === n) return true;
          const parts = n.split(" ");
          return parts.length === 2 && `${parts[1]} ${parts[0]}` === nk;
        });
      })
      .map((bk) => bk.name);

    setUnmatchedNames(unmatched);

    const matchedCount = attendanceData.length - unmatched.length;
    setSnackbar({
      open: true,
      message: `${Object.keys(mapping).length} E-Mails geladen. ${matchedCount}/${attendanceData.length} Mitglieder zugeordnet.${unmatched.length > 0 ? ` ${unmatched.length} ohne Zuordnung.` : ""}`,
      severity: unmatched.length > 0 ? "warning" : "success",
    });
  };

  const exportToExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Abrechnung");
    ws.addRow(["Name", ...daysOfWeek, "Bonus (EUR)", "Gesamtbetrag (EUR)"]);
    payments.forEach((row) => {
      ws.addRow([row.name, ...row.dailyPayments.map((p) => parseFloat(p).toFixed(2).replace(".", ",")), row.bonus.toFixed(2).replace(".", ","), row.total.replace(".", ",")]);
    });
    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "ballkids_abrechnung_uebersicht.xlsx");
  };

  const exportToTimesheets = async () => {
    const workbook = new ExcelJS.Workbook();
    const daysFormatted = [
      "11.04.2026", "12.04.2026", "13.04.2026", "14.04.2026", "15.04.2026",
      "16.04.2026", "17.04.2026", "18.04.2026", "19.04.2026"
    ];

    const getRandomStartTime = () => {
      const slots = ["10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "13:00"];
      return slots[Math.floor(Math.random() * slots.length)];
    };

    const getEndTime = (start, duration) => {
      const [h, m] = start.split(":").map(Number);
      const total = h * 60 + m + duration * 60;
      return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
    };

    payments.forEach((row) => {
      const sheet = workbook.addWorksheet(row.name.slice(0, 31));
      let totalHours = 0;

      sheet.getCell("B1").value = "Stundenzettel: MMP Event GmbH";
      sheet.getCell("B4").value = "Team: Mitarbeiter";
      sheet.getCell("C4").value = "Projekt: BMW Open by bitpanda 2026";
      sheet.getCell("B5").value = "Mitarbeiterdetails:";
      sheet.getCell("B7").value = "Abrechnungsbeginn Kalendermonat: April";
      sheet.getCell("C7").value = "Abrechnungsende Kalendermonat: April";
      sheet.getCell("C5").value = row.name;
      sheet.getCell("D5").value = lookupEmail(row.name) || "unbekannt@example.com";
      sheet.getCell("C10").value = `${hourlyRate.toFixed(2).replace(".", ",")} EUR`;
      sheet.getCell("B9").value = "Gesamtarbeitszeit pro Kalendermonat";
      sheet.getCell("C9").value = "Stundenlohn";
      sheet.getCell("D9").value = "Gesamtstundenanzahl pro Kalendermonat";
      sheet.getCell("E9").value = "Gesamtlohn pro Kalendermonat";

      const workedDayIndexes = row.dailyPayments
        .map((p, i) => ({ value: parseFloat(p), index: i }))
        .filter((entry) => entry.value > 0)
        .map((entry) => entry.index);

      if (workedDayIndexes.length > 0) {
        const firstDate = daysFormatted[workedDayIndexes[0]];
        const lastDate = daysFormatted[workedDayIndexes[workedDayIndexes.length - 1]];
        const formatDate = (d) => `${d.split(".")[0]}. April 2026`;
        sheet.getCell("B6").value = `Vertragsbeginn: ${formatDate(firstDate)}`;
        sheet.getCell("C6").value = `Vertragsende: ${formatDate(lastDate)}`;
      }

      sheet.getRow(11).values = ["", "Datumsangaben", "Arbeitsbeginn", "Pausenbeginn", "Pausenende", "Arbeitsende", "Arbeitsstunden", "Tageslohn"];

      const eligibleDays = row.dailyPayments.map((p, i) => ({ payment: parseFloat(p), index: i })).filter((e) => e.payment > 0);
      const bonus = parseFloat(row.bonus || 0);
      const bonusHours = bonus / hourlyRate;
      const bonusDayIndex = bonus > 0 && eligibleDays.length > 0 ? eligibleDays[Math.floor(Math.random() * eligibleDays.length)].index : null;

      let currentRowIdx = 12;
      for (let i = 0; i < row.dailyPayments.length; i++) {
        const base = parseFloat(row.dailyPayments[i]);
        let hours = base > 0 ? base / hourlyRate : 0;
        let pay = base;
        if (i === bonusDayIndex) { hours += bonusHours; pay += bonus; }
        if (hours === 0) continue;

        const start = getRandomStartTime();
        const end = getEndTime(start, hours);
        sheet.getCell(`B${currentRowIdx}`).value = daysFormatted[i];
        sheet.getCell(`C${currentRowIdx}`).value = start;
        sheet.getCell(`F${currentRowIdx}`).value = end;
        sheet.getCell(`G${currentRowIdx}`).value = String(hours).replace(".", ",");
        sheet.getCell(`H${currentRowIdx}`).value = `${pay.toFixed(2).replace(".", ",")} EUR`;
        totalHours += hours;
        currentRowIdx++;
      }

      sheet.getCell("B10").value = String(totalHours.toFixed(2)).replace(".", ",");
      sheet.getCell("D10").value = String(totalHours.toFixed(2)).replace(".", ",");
      sheet.getCell("E10").value = `${row.total.replace(".", ",")} EUR`;
      sheet.columns.forEach((col) => (col.width = 18));
      sheet.getCell("B1").font = { name: "Calibri", family: 4, size: 22, bold: true };

      let row1 = sheet.getRow(1);
      for (let col = 1; col <= 8; col++) {
        row1.getCell(col).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "8FAADC" } };
      }
      row1.commit();

      row1 = sheet.getRow(11);
      row1.eachCell({ includeEmpty: true }, (cell) => { cell.font = { name: "Calibri", family: 4, size: 11, bold: true }; });
      row1.commit();

      row1 = sheet.getRow(10);
      row1.eachCell({ includeEmpty: true }, (cell) => { cell.font = { name: "Calibri", family: 4, size: 20, bold: false }; });
      row1.commit();

      for (let i = 5; i <= 11; i++) { const r = sheet.getRow(i); r.height = 34; r.commit(); }
      sheet.getColumn("A").width = 1;
      sheet.getColumn("B").width = 50;
      sheet.getColumn("C").width = 50;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "ballkids_stundenzettel.xlsx");
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const totalSum = payments.reduce((sum, p) => sum + parseFloat(p.total), 0);

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Abrechnung</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Einstellungen werden automatisch gespeichert. Berechnung erfolgt live.
      </Typography>

      {/* Tages-Raten kompakt */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">Tagessätze</Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {rateOptions.map((rate) => (
              <Button key={rate} size="small" variant="outlined" onClick={() => setAllRates(rate)}>
                Alle {rate.toFixed(2).replace(".", ",")}
              </Button>
            ))}
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {daysOfWeek.map((day) => (
            <Box key={day} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" sx={{ minWidth: 60 }}>{day.split(".")[0]}.</Typography>
              <Select value={rates[day] || ""} onChange={(e) => handleRateChange(day, e.target.value)} size="small" sx={{ minWidth: 100 }} displayEmpty>
                <MenuItem value="" disabled>-</MenuItem>
                {rateOptions.map((rate) => (
                  <MenuItem key={rate} value={rate}>{rate.toFixed(2).replace(".", ",")}</MenuItem>
                ))}
              </Select>
            </Box>
          ))}
        </Box>
        <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
          <TextField label="Schwellenwert (h)" type="number" value={threshold || ""} onChange={(e) => setThreshold(parseFloat(e.target.value) || 0)} size="small" sx={{ width: 160 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">Stundenlohn (Stundenzettel):</Typography>
            <Select value={hourlyRate} onChange={(e) => setHourlyRate(parseFloat(e.target.value))} size="small" sx={{ minWidth: 120 }}>
              {HOURLY_RATE_OPTIONS.map((rate) => (
                <MenuItem key={rate} value={rate}>{rate.toFixed(2).replace(".", ",")} EUR</MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
        <Button variant="contained" onClick={exportToExcel} disabled={payments.length === 0}>Export Übersicht</Button>
        <input id="upload-excel" type="file" accept=".xlsx, .xls" onChange={handleExcelUpload} style={{ display: "none" }} />
        <label htmlFor="upload-excel">
          <Button variant="outlined" component="span">E-Mail-Zuordnung</Button>
        </label>
        <Button variant="contained" onClick={exportToTimesheets} disabled={Object.keys(emailMapping).length === 0 || payments.length === 0}>Stundenzettel</Button>
        <Button variant="contained" color="success" startIcon={<PublishIcon />} onClick={publishBilling} disabled={payments.length === 0}>
          Veroeffentlichen
        </Button>
        <Button variant="outlined" color="warning" onClick={unpublishBilling}>
          Zuruecknehmen
        </Button>
      </Box>

      {/* E-Mail Status */}
      {Object.keys(emailMapping).length > 0 && (
        <Alert severity={unmatchedNames.length > 0 ? "warning" : "success"} sx={{ mb: 2 }}>
          <strong>{Object.keys(emailMapping).length}</strong> E-Mail-Zuordnungen geladen.
          {unmatchedNames.length > 0 && (
            <>
              {" "}<strong>{unmatchedNames.length}</strong> Mitglieder ohne Zuordnung:
              <Box component="span" sx={{ display: "block", mt: 0.5, fontSize: "0.85rem" }}>
                {unmatchedNames.join(", ")}
              </Box>
            </>
          )}
        </Alert>
      )}

      {/* Gesamtsumme */}
      {payments.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Gesamtsumme: <strong>{totalSum.toFixed(2)} EUR</strong> für {payments.length} Ballkinder
        </Alert>
      )}

      {/* Ergebnis-Tabelle */}
      {payments.length > 0 && (
        <TableContainer component={Paper} sx={{ maxHeight: "70vh" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ position: "sticky", left: 0, bgcolor: "#1a2138", zIndex: 3 }}>Name</TableCell>
                {daysOfWeek.map((day) => (
                  <TableCell key={day} align="center">{day.split(".")[0]}.</TableCell>
                ))}
                <TableCell align="center">Bonus</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Gesamt</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((row) => (
                <TableRow key={row.name}>
                  <TableCell sx={{ position: "sticky", left: 0, bgcolor: "#1a2138", zIndex: 1 }}>{row.name}</TableCell>
                  {row.dailyPayments.map((amount, idx) => (
                    <TableCell key={idx} align="center" sx={{ color: parseFloat(amount) > 0 ? "inherit" : "text.secondary" }}>
                      {parseFloat(amount) > 0 ? `${amount}` : "-"}
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <Select value={bonuses[row.name] ?? 0} onChange={(e) => handleBonusChange(row.name, parseFloat(e.target.value))} size="small" sx={{ minWidth: 80 }}>
                      {bonusOptions.map((bonus) => (
                        <MenuItem key={bonus} value={bonus}>{bonus === 0 ? "0" : bonus.toFixed(2).replace(".", ",")}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>{row.total} EUR</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AttendanceBilling;
